import { useCallback, useEffect, useRef, useState } from "react";
import { imagineStore } from "@/stores/imagine.store";
import { createConsoleClient } from "@/lib/appwrite-client";
import {
  type RealtimeResponseEvent,
  DeploymentStatus,
  type Models,
} from "@appwrite.io/console";

export function useDeploymentLogs(
  deploymentId: string | null | undefined,
  initialLogs?: string,
  onComplete?: (deployment: Models.Deployment) => void,
  onLogsUpdate?: (logs: string) => void,
) {
  const [logs, setLogs] = useState<string>("");
  const projectId = imagineStore.use.projectId();
  const subscriptionRef = useRef<{ close: () => Promise<void> } | null>(null);

  useEffect(() => {
    if (initialLogs) {
      setLogs(initialLogs);
    }
  }, [initialLogs]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!deploymentId || !projectId) {
      return;
    }

    const siteId = `project-${projectId}`;
    const eventPattern = `sites.${siteId}.deployments.${deploymentId}.update`;

    const setupRealtime = async () => {
      try {
        const sdk = createConsoleClient();

        subscriptionRef.current = await sdk.realtime.subscribe(
          "console",
          (response) => {
            const isDeploymentEvent = response.events?.includes(eventPattern);

            if (!isDeploymentEvent) {
              return;
            }

            const deployment = response.payload as Models.Deployment;

            if (deployment.buildLogs) {
              setLogs(deployment.buildLogs);
              onLogsUpdate?.(deployment.buildLogs);
            }

            if (deployment.status && onComplete) {
              const isSuccess = deployment.status === DeploymentStatus.Ready;
              const isFailed = deployment.status === DeploymentStatus.Failed;
              const isCompleted = isSuccess || isFailed;

              if (isCompleted) {
                subscriptionRef.current?.close();
                subscriptionRef.current = null;
                onComplete(deployment);
              }
            }
          },
        );
      } catch (error) {
        console.error("Failed to connect to deployment logs:", error);
        setLogs(
          `Error: ${error instanceof Error ? error.message : "Failed to connect"}`,
        );
      }
    };

    setupRealtime();

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.close().then(() => {
          subscriptionRef.current = null;
        });
      }
    };
  }, [deploymentId, projectId]);

  return { logs };
}
